import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Input2 from '../../components/input/input-2/Input2';
import LoaderBar from '../../components/loader-bar/LoaderBar';
import { trackEvent } from '../../support-and-tracking/analytics-actions';
import { getPromotedRecipients } from '../organization-actions';
import styles from './organization-search.module.less';
import SearchResultItem from './SearchResultItem';
import _ from 'lodash';

const RegisteredOrganizationSearchResult = ({ recipients, handleOrganizationSelection, handleClick }) => {
    const elements =
        recipients &&
        recipients.map((recipient) => (
            <SearchResultItem
                key={recipient.id}
                image={recipient.img_url}
                name={recipient.name}
                organizationNumber={recipient.organization_number}
                event={recipient.event_id ? recipient : null}
                handleOrganizationSelection={handleOrganizationSelection}
                handleClick={handleClick}
                hideOrgNumber={recipient.profile?.hideOrgNumber}
                promoted
            />
        ));
    return elements ? elements : null;
};

const OrganizationFromExternalSourceResult = ({ organizations, handleOrganizationSelection }) => {
    return (
        organizations &&
        organizations.map((organization) => (
            <SearchResultItem
                key={organization.id}
                name={organization.name}
                organizationNumber={organization.id}
                handleOrganizationSelection={handleOrganizationSelection}
            />
        ))
    );
};

const OrganizationSearchResult = ({
    spleisOrganizations = [],
    organizationsFromExternalSource = [],
    defaultRecipients = [],
    input,
    handleOrganizationSelection,
    focused,
}) => {
    const searchResultElement = useRef();

    useEffect(() => {
        if (focused && searchResultElement?.current) {
            searchResultElement.current.childNodes[0].focus();
        }
    }, [focused]);

    if (!input && defaultRecipients.length === 0) return null;

    const organizationsFromExternalSourceNotAlredyRegistered = organizationsFromExternalSource.filter(
        (organization) => !_.find(spleisOrganizations, { organization_number: organization.id })
    );

    return (
        <ul className={styles.searchResultContainer} ref={searchResultElement}>
            <>
                <RegisteredOrganizationSearchResult
                    recipients={spleisOrganizations}
                    handleOrganizationSelection={handleOrganizationSelection}
                />
                <OrganizationFromExternalSourceResult
                    organizations={organizationsFromExternalSourceNotAlredyRegistered}
                    handleOrganizationSelection={handleOrganizationSelection}
                />
            </>
            {spleisOrganizations.length === 0 && organizationsFromExternalSourceNotAlredyRegistered.length === 0 && (
                <>
                    {!!input && (
                        <li className={styles.searchResultInformation}>
                            {`Fant ingen som passet søket ditt${
                                defaultRecipients.length > 0 ? ', men her er noen forslag:' : ''
                            }`}
                        </li>
                    )}
                    <RegisteredOrganizationSearchResult
                        recipients={defaultRecipients}
                        handleOrganizationSelection={handleOrganizationSelection}
                        promoted
                    />
                </>
            )}
        </ul>
    );
};

const OrganizationSearch = ({
    handleOrganizationSelection,
    handlePayoutToChange,
    hidePromotedOrganizations,
    fieldName,
    projectHasProducts,
    onlyCharities,
    inputRef,
}) => {
    const [input, setInput] = useState('');
    const [isFetchingFromSpleis, setIsFetchingFromSpleis] = useState(false);
    const [isFetchingOrganizationsFromExternalSource, setIsFetchingOrganizationsFromExternalSource] = useState(false);
    const [focused, setFocused] = useState(false);
    const [spleisOrganizationSearchResults, setSpleisOrganizationSearchResults] = useState([]);
    const [organizationsFromExternalSourceResult, setOrganizationsFromExternalSourceResult] = useState([]);
    const promotedRecipients = useSelector((state) => state.organizations.promotedRecipients);

    const baseURL = useSelector((state) => state.baseURL);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getPromotedRecipients());
    }, [dispatch]);

    const searchinRegisteredOrganizations = async (searchInput) => {
        if (searchInput.length < 2) {
            setSpleisOrganizationSearchResults([]);
            setIsFetchingFromSpleis(false);
            return;
        }
        setIsFetchingFromSpleis(true);

        const response = await fetch(
            `${baseURL}/api/public/search/recipient?q=${searchInput}${onlyCharities ? '&onlyCharities=true' : ''}`
        );
        if (!response.ok) {
            setSpleisOrganizationSearchResults([]);
            setIsFetchingFromSpleis(false);
            return;
        }
        const result = await response.json();

        const organizations = [
            ...result.events
                .filter((o) => !(projectHasProducts && o.customPriceFunction))
                .map((event) => ({ ...event, event_id: event.id })),
            ...result.organizations
                .filter((o) => {
                    return !result.events.find((e) => e.organization_id === o.id);
                })
                .filter((o) => !(projectHasProducts && o.customPriceFunction)),
        ];

        setSpleisOrganizationSearchResults(organizations);
        setIsFetchingFromSpleis(false);
    };

    const handleInputChange = (input) => {
        setInput(input);
        const trimmedInput = encodeURI(input.trim());
        getOrganizationsFromExternalSource(trimmedInput);
        searchinRegisteredOrganizations(trimmedInput);
    };

    const getOrganizationsFromExternalSource = async (trimmedInput) => {
        if (trimmedInput.length < 2) {
            setOrganizationsFromExternalSourceResult([]);
            setIsFetchingOrganizationsFromExternalSource(false);
            return;
        }
        setIsFetchingOrganizationsFromExternalSource(true);

        const response = await fetch(
            `${baseURL}/api/public/search/organization-register?q=${trimmedInput}${
                onlyCharities ? '&onlyCharities=true' : ''
            }`
        );
        if (!response.ok) {
            setOrganizationsFromExternalSourceResult([]);
            setIsFetchingOrganizationsFromExternalSource(false);
            return;
        }
        const result = await response.json();
        setOrganizationsFromExternalSourceResult(result);
        setIsFetchingOrganizationsFromExternalSource(false);
    };

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown' && e.target.nextElementSibling && e.target.nextElementSibling.childNodes[0]) {
            e.preventDefault();
            setFocused(true);
        } else {
            setFocused(false);
        }
    };

    return (
        <div className={styles.searchWrapper}>
            <Input2
                wrapperClass={styles.searchInput}
                name={fieldName}
                placeholder="Organisasjonsnummer/-navn"
                value={input}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={onKeyDown}
                onFocus={() => {
                    trackEvent('org', 'register-search');
                    if (handlePayoutToChange && handleInputChange != null) {
                        handlePayoutToChange('', null);
                    }
                }}
                extraMargin={false}
                inputRef={inputRef}
            />
            {isFetchingOrganizationsFromExternalSource ||
                (isFetchingFromSpleis && <LoaderBar className={styles.loader} />)}
            <OrganizationSearchResult
                spleisOrganizations={spleisOrganizationSearchResults}
                organizationsFromExternalSource={organizationsFromExternalSourceResult}
                defaultRecipients={hidePromotedOrganizations ? [] : promotedRecipients}
                input={input}
                handleOrganizationSelection={handleOrganizationSelection}
                focused={focused}
            />
        </div>
    );
};

export default OrganizationSearch;
